import { Refraction } from '../../schema'
import { StrapiID } from './common'
import { PricesRegion } from './price'

export const ConditionSelectors = {
  eq: '=',
  neq: '!=',
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
} as const

export type BaseConditionAttributes = {
  id: StrapiID
  errorKey?: string | null
  selector?: keyof typeof ConditionSelectors
  param?: string
  target?: string
}

export type BaseConditionComponent = {
  __component: 'conditions.basic'
} & BaseConditionAttributes

type ConditionRelation = {
  name: string
  id: StrapiID
}

export type RelationConditionAttributes = {
  id: StrapiID
  errorKey?: string | null
  negation: boolean
  // Some strapi wierdness here during edit its an array, but on fetch a single object
  condition?: ConditionRelation | ConditionRelation[]
}

export type RelationConditionComponent = {
  __component: 'conditions.relation'
} & RelationConditionAttributes

export type RefractionConditionAttributes = {
  id: StrapiID
  errorKey?: string | null
  sphMin?: number
  sphMax?: number
  cylMin?: number
  cylMax?: number
  totalMin?: number
  totalMax?: number
  addMin?: number
  addMax?: number
  withEquals?: boolean
  singleSide?: boolean | null
}

export type RefractionConditionComponent = {
  __component: 'conditions.refraction'
} & RefractionConditionAttributes

export type StrengthConditionAttributes = {
  id: StrapiID
  errorKey?: string | null
  min?: number
  max?: number
  withEquals?: boolean
  singleSide?: boolean | null
}

export type StrengthConditionComponent = {
  __component: 'conditions.strength'
} & StrengthConditionAttributes

export type PriceConditionAttributes = {
  id: StrapiID
  errorKey?: string | null
  target?: string
  selector?: keyof typeof ConditionSelectors
  values?: PricesRegion
}

export type PriceConditionComponent = {
  __component: 'conditions.price'
} & PriceConditionAttributes

export type NestedConditionAttributes = {
  id: string | number
  selector?: 'and' | 'or'
  errorKey?: string | null
  basic?: BaseConditionAttributes[]
  relation?: RelationConditionAttributes[]
  refraction?: RefractionConditionAttributes[]
  strength?: StrengthConditionAttributes[]
}

export type NestedConditionComponent = {
  __component: 'conditions.nested'
} & NestedConditionAttributes

export type ConditionComponent =
  | BaseConditionComponent
  | RelationConditionComponent
  | RefractionConditionComponent
  | NestedConditionComponent
  | StrengthConditionComponent
  | PriceConditionComponent

/// Parsed conditions:
export type ConditionTargetValue =
  | number
  | string
  | boolean
  | undefined
  | Refraction

export type BasicCondition = {
  param: string
  selector: keyof typeof ConditionSelectors
  target: number | string | boolean
  errorKey?: string
}

export type ConditionValues = {
  [key: string]: ConditionTargetValue
}

export type ConditionRange = [number | null, number | null]

export type RefractionCondition = {
  selector: 'refraction'
  errorKey?: string
  target: {
    sph?: ConditionRange
    cyl?: ConditionRange
    total?: ConditionRange
    add?: ConditionRange
    withEquals?: boolean
    singleSide?: boolean
  }
}

export type StrengthCondition = {
  selector: 'strength'
  errorKey?: string
  target: {
    range: ConditionRange
    withEquals?: boolean
    singleSide?: boolean
  }
}

export type PriceCondition = {
  selector: 'price'
  errorKey?: string
  target: {
    variable: string
    selector: keyof typeof ConditionSelectors
    values: PricesRegion
  }
}

export type RelationCondition = {
  selector: 'relation'
  errorKey?: string
  negation?: boolean
  value: string
}

export type NegationCondition = {
  selector: 'not'
  errorKey?: string
  value:
    | BasicCondition
    | NestedCondition
    | RefractionCondition
    | NegationCondition
    | StrengthCondition
    | PriceCondition
}

type NestedCondition = {
  selector: 'and' | 'or'
  errorKey?: string
  value: (
    | BasicCondition
    | NestedCondition
    | RefractionCondition
    | RelationCondition
    | StrengthCondition
    | PriceCondition
  )[]
}

export type ResolvedCondition =
  | BasicCondition
  | NestedCondition
  | RefractionCondition
  | NegationCondition
  | StrengthCondition
  | PriceCondition

export type Condition = ResolvedCondition | RelationCondition

export type ConditionDictionary = Record<string, Condition>
